import {
  IconChevronDown,
  IconChevronUp,
  IconInfoCircle,
  IconSelector,
} from '@tabler/icons-react';
import fileDownload from 'js-file-download';
import React, { useCallback, useMemo, useState } from 'react';
import { ChatTableSpecDto } from 'src/api/FiretrolApiClient';
import {
  buildTableCsv,
  chatCsvFileName,
  CSV_MIME_TYPE,
} from 'src/app/chat/chatCsv';
import {
  CHAT_TABLE_COLUMN_MIN_WIDTH,
  CHAT_TABLE_NUMERIC_CELL_STYLE,
  CHAT_TABLE_SCROLL_AREA_PROPS,
  ChatTableSortDirection,
  formatTableCell,
  isNumericTableFormat,
  sortTableRowOrder,
  TABLE_EMPTY_TEXT,
  tableTruncationText,
} from 'src/app/chat/chatTableFormat';
import { EntityRefLink } from 'src/app/chat/EntityRefLink';
import { useChatQueryCsvDownload } from 'src/app/chat/useChatQueryCsvDownload';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { UnstyledButton } from 'src/design/atoms/UnstyledButton';
import { Table } from 'src/design/molecules/Table';

export interface IChatTableProps {
  spec: ChatTableSpecDto;
  /** With `canExport`, lets a truncated table download its full query result instead of only the shown rows. */
  conversationId?: string | null;
  /** False while the reply streams or after it failed: the server saves the query records with the finished reply. */
  canExport?: boolean;
  onRefClick?: () => void;
}

export interface IChatTableSortState {
  columnIndex: number;
  direction: ChatTableSortDirection;
}

type TableRow = readonly unknown[];

const TABLE_MAX_HEIGHT = 360;

export const FULL_RESULT_CSV_TOOLTIP = 'All query columns, up to 1,000 rows.';

const ARIA_SORT: Record<ChatTableSortDirection, 'ascending' | 'descending'> = {
  asc: 'ascending',
  desc: 'descending',
};

const nextSort = (
  current: IChatTableSortState | null,
  columnIndex: number,
): IChatTableSortState | null => {
  if (current?.columnIndex !== columnIndex) {
    return { columnIndex, direction: 'asc' };
  }
  return current.direction === 'asc'
    ? { columnIndex, direction: 'desc' }
    : null;
};

const SortIcon: React.FC<{ direction: ChatTableSortDirection | null }> = ({
  direction,
}) => {
  const props = { size: 12, 'aria-hidden': true, style: { flexShrink: 0 } };
  if (direction === 'asc') return <IconChevronUp {...props} />;
  if (direction === 'desc') return <IconChevronDown {...props} />;
  return <IconSelector {...props} />;
};

const FullResultCsvButton: React.FC<{
  conversationId: string;
  auditId: string;
  title: string;
}> = ({ conversationId, auditId, title }) => {
  const { download, downloading } = useChatQueryCsvDownload(
    conversationId,
    auditId,
  );
  return (
    <Tooltip label={FULL_RESULT_CSV_TOOLTIP}>
      <BasicButton
        size="compact-xs"
        variant="subtle"
        color="gray"
        aria-label={`Download full query result (CSV) of ${title}`}
        loading={downloading}
        onClick={() => void download()}
      >
        Download full query result (CSV)
      </BasicButton>
    </Tooltip>
  );
};

/** @registry Renders an AI-assistant table spec with format-aware cells (linked to their record where the column has server-built refs), click-to-sort headers (ascending, descending, original), a truncation notice, and a CSV download of the shown rows or, for a truncated saved reply, the full query result. */
export const ChatTable = React.memo<IChatTableProps>(
  ({ spec, conversationId, canExport = false, onRefClick }) => {
    const [sort, setSort] = useState<IChatTableSortState | null>(null);
    const columns = useMemo(() => spec.columns ?? [], [spec.columns]);
    const numericColumns = useMemo(
      () => columns.map((column) => isNumericTableFormat(column.format)),
      [columns],
    );
    const rows: readonly TableRow[] = useMemo(
      () => spec.rows ?? [],
      [spec.rows],
    );
    const rowOrder = useMemo(
      () =>
        sort
          ? sortTableRowOrder(
              rows,
              sort.columnIndex,
              columns[sort.columnIndex]?.format ?? 'text',
              sort.direction,
            )
          : rows.map((_, index) => index),
      [rows, columns, sort],
    );
    const shownRows = useMemo(
      () => rowOrder.map((index) => rows[index]),
      [rows, rowOrder],
    );
    const label = spec.title || 'table';
    const fullResultAuditId =
      spec.truncated && canExport && conversationId ? spec.auditId : undefined;

    const downloadShownRows = useCallback(
      () =>
        fileDownload(
          buildTableCsv(spec, shownRows),
          chatCsvFileName('table', new Date()),
          CSV_MIME_TYPE,
        ),
      [spec, shownRows],
    );

    return (
      <Stack gap="xs" w="100%" style={{ minWidth: 0 }} data-testid="chat-table">
        {spec.title && (
          <Text fw={600} size="sm" c="bright">
            {spec.title}
          </Text>
        )}
        {rows.length === 0 ? (
          !spec.truncated && (
            <Text size="xs" c="dimmed">
              {TABLE_EMPTY_TEXT}
            </Text>
          )
        ) : (
          <Table.ScrollContainer
            minWidth={columns.length * CHAT_TABLE_COLUMN_MIN_WIDTH}
            maxHeight={TABLE_MAX_HEIGHT}
            scrollAreaProps={CHAT_TABLE_SCROLL_AREA_PROPS}
          >
            <Table withTableBorder stickyHeader fz="xs" verticalSpacing={4}>
              <Table.Thead>
                <Table.Tr>
                  {columns.map((column, columnIndex) => {
                    const numeric = numericColumns[columnIndex];
                    const direction =
                      sort?.columnIndex === columnIndex ? sort.direction : null;
                    return (
                      <Table.Th
                        key={columnIndex}
                        ta={numeric ? 'right' : undefined}
                        aria-sort={direction ? ARIA_SORT[direction] : 'none'}
                      >
                        <UnstyledButton
                          onClick={() =>
                            setSort((current) => nextSort(current, columnIndex))
                          }
                          style={{
                            fontSize: 'inherit',
                            fontWeight: 'inherit',
                          }}
                        >
                          <Group
                            gap={4}
                            wrap="nowrap"
                            justify={numeric ? 'flex-end' : 'flex-start'}
                          >
                            <Text span inherit c="bright">
                              {column.label}
                            </Text>
                            <SortIcon direction={direction} />
                          </Group>
                        </UnstyledButton>
                      </Table.Th>
                    );
                  })}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rowOrder.map((sourceIndex, rowIndex) => (
                  <Table.Tr key={rowIndex}>
                    {columns.map((column, columnIndex) => {
                      const numeric = numericColumns[columnIndex];
                      return (
                        <Table.Td
                          key={columnIndex}
                          ta={numeric ? 'right' : undefined}
                          style={
                            numeric ? CHAT_TABLE_NUMERIC_CELL_STYLE : undefined
                          }
                        >
                          <EntityRefLink
                            href={column.refs?.[sourceIndex]}
                            onRefClick={onRefClick}
                          >
                            {formatTableCell(
                              rows[sourceIndex][columnIndex],
                              column.format,
                            )}
                          </EntityRefLink>
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {spec.truncated && (
          <Group gap={4} wrap="nowrap" align="flex-start">
            <IconInfoCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <Text size="xs" c="dimmed">
              {tableTruncationText(rows.length)}
              {sort && ' Sorting applies to these rows only.'}
            </Text>
          </Group>
        )}
        {(fullResultAuditId || rows.length > 0) && (
          <Group gap="xs">
            {conversationId && fullResultAuditId ? (
              <FullResultCsvButton
                conversationId={conversationId}
                auditId={fullResultAuditId}
                title={label}
              />
            ) : (
              <BasicButton
                size="compact-xs"
                variant="subtle"
                color="gray"
                aria-label={`Download CSV of ${label}`}
                onClick={downloadShownRows}
              >
                Download CSV
              </BasicButton>
            )}
          </Group>
        )}
      </Stack>
    );
  },
);
